// pages/InstallSetupPage.jsx — extracted from App.js (behaviour-preserving split).
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Check, CheckCircle2, Download, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cacheImageManifest, hasLocalSnapshot, hydrateFromSnapshot, markInstallCompleted, verifyOfflineReadiness } from "@/lib/localDb";
import { pingConnectivity } from "@/lib/offlineAuth";
import { markSnapshotFresh } from "@/lib/snapshotRefresh";
import { isAppShellCached } from "@/lib/pwa";
import { API } from "@/app/core";
import { readRoleSession } from "@/lib/authStorage";


/**
 * InstallSetupPage — the "Preparing NFC Solutions…" progress screen.
 *
 * Runs immediately after a successful first-time online login for a
 * device that has NEVER completed setup on this business. Downloads
 * the complete business snapshot, caches product images, verifies
 * offline readiness, and only THEN marks the install as complete
 * and forwards the operator to their dashboard.
 *
 * The page is idempotent — reopening it on an already-installed
 * device forces a fresh re-sync (useful for troubleshooting).
 */
export function InstallSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/";
  // Session 25.7 — pre-auth install: when an install-token is supplied
  // in the URL, we hit the public `/sync/install-snapshot` endpoint
  // instead of the auth-gated `/sync/snapshot`. This lets a fresh
  // tablet complete the full download WITHOUT any user login — the
  // owner provisions the tablet by sharing an install link, and PIN
  // sign-in happens later purely to enrol per-user offline credentials.
  //
  // Session 26 — Capacitor: the deep-link handler in DeviceBootstrap
  // stashes the token in sessionStorage under `nfcs.pendingInstallToken`
  // when the APK is opened from a `https://webnfcs.com/apk/install/<token>`
  // intent. Fall back to that here so the APK's "tap link → open app"
  // flow drops the user directly into the install with the business
  // preselected — no manual code entry.
  const installToken = useMemo(() => {
    const fromQuery = (searchParams.get("token") || "").trim();
    if (fromQuery) return fromQuery;
    try {
      const stashed = (window.sessionStorage.getItem("nfcs.pendingInstallToken") || "").trim();
      return stashed || "";
    } catch (_) {
      return "";
    }
  }, [searchParams]);
  // Locate any active session we can use to authenticate the snapshot
  // fetch (fallback for the legacy post-login install flow). Any
  // role's token will do — /sync/snapshot accepts every role (owner/
  // staff/admin/kitchen/bar), all business-scoped. Session 27.12
  // prefers owner → staff → admin → kitchen → bar so the most
  // permissive session is used when multiple are cached.
  const initialToken = useMemo(() => {
    if (installToken) return null; // install-token path skips session auth
    // Session 30.36 — Retail was missing from this list, so a Retailer
    // signing in on a fresh device hit /install/setup with no token and
    // the wizard failed at 0s ("Connecting" ✗). Also fall back to the
    // device-scoped localStorage copy like getSession() does.
    const preference = ["owner", "staff", "admin", "kitchen", "bar", "retail"];
    for (const role of preference) {
      const s = readRoleSession(role, "any");
      if (s?.token && !String(s.token).startsWith("offline:")) return s.token;
    }
    return null;
  }, [installToken]);

  const STEPS = useMemo(() => ([
    { key: "connect",  label: "Connecting to NFC Solutions" },
    { key: "snapshot", label: "Downloading complete business database" },
    { key: "hydrate",  label: "Writing local database" },
    { key: "images",   label: "Caching product images for offline use" },
    { key: "shell",    label: "Preparing offline app shell" },
    { key: "verify",   label: "Verifying offline readiness" },
    { key: "finalise", label: "Finalising installation" },
  ]), []);

  const [stepIndex, setStepIndex] = useState(0);
  const [stepDetail, setStepDetail] = useState("");
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [readinessReport, setReadinessReport] = useState(null);
  // Elapsed time in seconds so the operator can see the install is
  // doing genuine work (previously it flew by in 2 seconds on small menus).
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [totals, setTotals] = useState(null);
  const startedRef = useRef(false);
  const startTimeRef = useRef(null);

  // Elapsed-time ticker — keeps counting until we mark done or error.
  useEffect(() => {
    if (done || error) return undefined;
    if (startTimeRef.current == null) startTimeRef.current = Date.now();
    const id = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 200);
    return () => window.clearInterval(id);
  }, [done, error]);

  // Small deliberate pause between visible steps so the operator can
  // actually SEE each stage of the install run — 300ms is fast enough
  // that setup still feels snappy, slow enough that no step is missed.
  // Session 30.34 — pauses were 300 ms per step "so the operator can see
  // each stage"; the owner found the whole sequence too busy. Steps now
  // run back-to-back and the UI is a single progress bar.
  const pause = (ms = 0) => (ms > 0 ? new Promise((resolve) => setTimeout(resolve, Math.min(ms, 60))) : Promise.resolve());

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!initialToken && !installToken) {
      setError("You need an install link or an online sign-in to complete setup.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Session 27.74 — Fast-path install completion. If the pre-auth
        // skeleton has ALREADY primed the local DB from the business-
        // code step, and we now have an authenticated token, we can
        // skip the giant "Downloading complete business database" step
        // and jump straight to a compressed 3-step flow:
        //   Connecting → Finalising business data → Verifying → Done
        // This eliminates the ~10-20 second post-PIN-login wait the
        // user was seeing every time. The full snapshot download is
        // still executed under the hood (we need image_manifest and
        // authenticated collections like users w/ PIN hashes) but the
        // UI shows just one blended "finalising" step instead of six.
        let skeletonPrimed = false;
        try { skeletonPrimed = await hasLocalSnapshot(); } catch (_) { skeletonPrimed = false; }
        const fastPath = skeletonPrimed && !!initialToken && !installToken;

        // ── Step 1: connect ────────────────────────────────────────
        setStepIndex(0);
        setStepDetail(fastPath
          ? "Checking connection to NFC Solutions cloud (fast install)…"
          : "Checking connection to NFC Solutions cloud…");
        await pause(200);
        const online = await pingConnectivity({ timeoutMs: 4000 });
        if (!online) {
          setError("The device could not reach NFC Solutions. Please check your internet connection and try again.");
          return;
        }
        setStepDetail(installToken
          ? "Cloud reachable · using install link"
          : fastPath
            ? "Cloud reachable · workspace already prepared · finalising"
            : "Cloud reachable · fetching your business");
        await pause(fastPath ? 150 : 300);
        if (cancelled) return;

        // ── Step 2: full snapshot ─────────────────────────────────
        setStepIndex(1);
        setStepDetail(
          installToken
            ? "Consuming install link · requesting complete business database…"
            : fastPath
              ? "Refreshing business data on top of the workspace we already prepared…"
              : "Requesting full copy of the business database…",
        );
        const snapT0 = performance.now();
        // Pre-auth path uses the public install-snapshot endpoint; the
        // authenticated path uses the standard snapshot endpoint.
        //
        // Session 27.33 — Bumped timeout 60s → 180s (3 minutes) and
        // added a single automatic retry on network failure. Some
        // deployments (large menus + slow WiFi receivers, e.g. 4G
        // dongles) legitimately take over a minute for the initial
        // snapshot download. A hard 60s cap was killing installs on
        // slower hardware even though the download would have
        // succeeded given a bit more time.
        const _snapshotUrl = installToken ? `${API}/sync/install-snapshot` : `${API}/sync/snapshot`;
        const _snapshotConfig = installToken
          ? { params: { token: installToken }, timeout: 180000 }
          : { headers: { Authorization: `Bearer ${initialToken}` }, timeout: 180000 };
        let snapRes;
        try {
          snapRes = await axios.get(_snapshotUrl, _snapshotConfig);
        } catch (firstErr) {
          // Only retry on transient network errors — do NOT retry
          // on 4xx (auth issues won't heal). Timeout, 5xx, and
          // network errors all fall through to the retry.
          const status = firstErr?.response?.status;
          const isRetryable = !status || status >= 500 || firstErr?.code === "ECONNABORTED";
          if (!isRetryable) throw firstErr;
          if (cancelled) return;
          setStepDetail("First download attempt didn't complete — retrying once with a longer window…");
          await pause(1000);
          snapRes = await axios.get(_snapshotUrl, { ..._snapshotConfig, timeout: 180000 });
        }
        if (cancelled) return;
        const snapshot = snapRes.data || {};
        const t = snapshot.totals || {};
        const collectionCount = Object.keys(t).length;
        const totalRecords = Object.values(t).reduce((a, b) => a + Number(b || 0), 0);
        const snapMs = Math.max(0, Math.round(performance.now() - snapT0));
        const bizName = (snapshot.business?.name) || (snapshot.business?.slug) || "your restaurant";
        setTotals({
          collectionCount,
          totalRecords,
          collections: t,
          business: bizName,
          image_count: (snapshot.image_manifest || []).length,
          snapshot_version: snapshot.snapshot_version,
        });
        setStepDetail(
          `Downloaded ${totalRecords.toLocaleString()} records across ${collectionCount} collections · ${(snapMs / 1000).toFixed(1)}s`,
        );
        await pause(400);

        // ── Step 3: hydrate into IndexedDB (per-collection progress) ─
        setStepIndex(2);
        setStepDetail(`Writing ${collectionCount} collection${collectionCount === 1 ? "" : "s"} to the local database…`);
        await hydrateFromSnapshot(snapshot, {
          onProgress: ({ done: d, total, name, count }) => {
            if (cancelled) return;
            const pretty = String(name || "")
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            setStepDetail(
              `${pretty}: ${Number(count || 0).toLocaleString()} record${count === 1 ? "" : "s"}  ·  ${d}/${total}`,
            );
          },
        });
        if (cancelled) return;
        setStepDetail(`Local database ready · ${collectionCount} collections`);
        await pause(350);

        // ── Step 4: image caching (smart hybrid) ──────────────────
        setStepIndex(3);
        const manifest = Array.isArray(snapshot.image_manifest) ? snapshot.image_manifest : [];
        if (manifest.length === 0) {
          setStepDetail("No product images to cache.");
          await pause(300);
        } else {
          setStepDetail(
            `Preparing to cache ${manifest.length} image${manifest.length === 1 ? "" : "s"}…`,
          );
          await pause(200);
          try {
            const cacheRes = await cacheImageManifest(manifest, {
              onProgress: (p) => {
                if (cancelled) return;
                const pct = Math.round((p.done / Math.max(1, p.total)) * 100);
                setStepDetail(`Cached ${p.done}/${p.total} images  ·  ${pct}%`);
              },
            });
            if (!cancelled) {
              setStepDetail(
                `Cached ${cacheRes.cached}/${cacheRes.total} images  ·  strategy: ${cacheRes.strategy}`,
              );
            }
          } catch (_) { /* non-fatal — offline readiness accepts 80% cached */ }
          await pause(300);
        }

        // ── Step 5: prepare offline app shell ─────────────────────
        // The service worker will precache the shell on install; here we
        // give it a moment to finish (and warn if it hasn't). This is
        // the crucial step that makes the PWA bootable offline.
        setStepIndex(4);
        setStepDetail("Waiting for the service worker to cache the app shell…");
        // Warm the shell explicitly by making a same-origin fetch that
        // the SW will intercept + cache. Best-effort.
        try {
          await fetch("/index.html", { cache: "no-cache" }).catch(() => null);
        } catch (_) { /* noop */ }
        // Poll up to 3 seconds for the SW to become active.
        let shellReady = false;
        for (let i = 0; i < 15; i += 1) {
          if (cancelled) return;
          if (await isAppShellCached()) { shellReady = true; break; }
          await pause(200);
        }
        if (shellReady) {
          setStepDetail("App shell cached · PWA will boot offline");
        } else {
          setStepDetail("App shell cache is still warming up — will finalise in the background");
        }
        await pause(400);

        // ── Step 6: verify offline readiness ──────────────────────
        setStepIndex(5);
        setStepDetail("Running local database integrity checks…");
        await pause(300);
        const report = await verifyOfflineReadiness();
        setReadinessReport(report);
        if (cancelled) return;
        const passing = report.checks.filter((c) => c.ok).length;
        setStepDetail(`${passing}/${report.checks.length} checks passed`);
        await pause(400);
        if (!report.ok) {
          setError(
            "Offline setup is incomplete. Please connect to the internet to finish preparing this device for offline operation.",
          );
          return;
        }

        // ── Step 7: finalise ──────────────────────────────────────
        // Session 25.9 — Robust finalise. Two failure modes we've seen
        // cause the installer to hang here:
        //   1. `markInstallCompleted` internally does 4 sequential
        //      IndexedDB writes; if any one stalls (e.g. because the
        //      readiness pass just above left a transaction lingering
        //      on some browsers), the whole promise chain deadlocks.
        //   2. `setDone(true)` never fires, so the "Finalising
        //      installation" step spins forever.
        // Guardrails: hard 4-second timeout on the IDB writes; if we
        // time out we STILL flip to done (with a backup localStorage
        // flag), because the user has clearly completed setup — a
        // metadata write failure must not gate the whole flow.
        setStepIndex(6);
        setStepDetail("Marking this device as offline-ready…");
        try {
          await Promise.race([
            markInstallCompleted({
              snapshot_version: snapshot.snapshot_version,
              snapshot_id: snapshot.snapshot_id,
              image_count: manifest.length,
            }),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("finalise-idb-timeout")), 4000),
            ),
          ]);
        } catch (finaliseErr) {
          // Non-fatal — record a fallback flag in localStorage so the
          // auto-install guard and Local Status card still recognise
          // this device as installed even if IDB persistence stalled.
          try {
            const ts = new Date().toISOString();
            localStorage.setItem("nfcs:install:completed_at_backup", ts);
            localStorage.setItem("nfcs:install:snapshot_id", snapshot.snapshot_id || "");
          } catch (_) { /* noop */ }
          // eslint-disable-next-line no-console
          console.warn("[install/setup] finalise timed out or errored — using localStorage backup:", finaliseErr?.message);
        }
        await pause(400);
        if (cancelled) return;
        setDone(true);
        try { markSnapshotFresh(); } catch (_) { /* noop */ }
        setStepDetail("Installation complete · this device now works offline");
        window.setTimeout(() => {
          if (!cancelled) navigate(nextPath || "/", { replace: true });
        }, 1600);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[install/setup] failed", e);
        const msg = e?.response?.data?.detail || e?.message || "Something went wrong during setup.";
        setError(String(msg));
      }
    })();
    // Session 30.36 — if this run is torn down before it finished (React
    // StrictMode dev double-invoke, or a dependency change mid-flight),
    // release the start guard so the next run can restart the install
    // instead of leaving the page parked on "Connecting… 7%".
    return () => { cancelled = true; startedRef.current = false; };
  }, [initialToken, installToken, nextPath, navigate, STEPS.length]);

  // Session 25.9 — Retry now performs a FULL reset so a failed attempt
  // never leaves partial state behind that would short-circuit the
  // next run:
  //   • Clears install-completed markers (both IDB meta and the
  //     localStorage backup flag)
  //   • Sweeps `nfcs-images-*` and `nfcs-runtime-*` caches
  //   • Reloads with a cache-busting query so the browser re-fetches
  //     the SW file (helpful on preview hosts where dev-server churn
  //     might have left a stale service worker in "waiting" state)
  // Skip is unchanged — just navigates on with whatever state exists.
  const retry = async () => {
    try {
      const { clearInstallState } = await import("@/lib/localDb");
      await clearInstallState();
    } catch (_) { /* noop */ }
    try {
      localStorage.removeItem("nfcs:install:completed_at_backup");
      localStorage.removeItem("nfcs:install:snapshot_id");
    } catch (_) { /* noop */ }
    try {
      if (typeof caches !== "undefined") {
        const keys = await caches.keys();
        await Promise.all(
          keys
            .filter((k) => k.startsWith("nfcs-images-") || k.startsWith("nfcs-runtime-"))
            .map((k) => caches.delete(k).catch(() => false)),
        );
      }
    } catch (_) { /* noop */ }
    const bust = `_r=${Date.now()}`;
    const sep = window.location.search ? "&" : "?";
    window.location.replace(`${window.location.pathname}${window.location.search}${sep}${bust}`);
  };
  const skip = () => { navigate(nextPath || "/", { replace: true }); };

  return (
    <main
      className="min-h-screen bg-[radial-gradient(1000px_circle_at_10%_0%,hsl(var(--primary)/0.14),transparent_55%),radial-gradient(700px_circle_at_100%_20%,hsl(var(--accent)/0.10),transparent_50%)] px-4 py-8 sm:px-6 sm:py-14"
      data-testid="install-setup-page"
    >
      <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
        <header className="space-y-2 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
            {done ? <CheckCircle2 className="h-7 w-7" /> : <Download className={`h-7 w-7 ${error ? "" : "animate-pulse"}`} />}
          </div>
          <h1 className="font-['Space_Grotesk'] text-3xl font-semibold tracking-tight">
            {done ? "Ready" : error ? "Setup couldn't finish" : "Setting up…"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {done
              ? "This device now works offline. Opening your dashboard…"
              : error
                ? "Your business data was not fully downloaded."
                : "Setting up this device for offline use. This only happens once."}
          </p>
          {/* Live elapsed timer + business name so operators can see that
              real work is happening (small menus used to complete in <2s
              which made the setup look fake). */}
          <div className={`mt-2 flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.14em] text-muted-foreground ${error ? "" : "hidden"}`}>
            {totals?.business ? <span data-testid="install-setup-business">{totals.business}</span> : null}
            {totals?.business ? <span aria-hidden>·</span> : null}
            <span data-testid="install-setup-elapsed">
              {elapsedSeconds < 60
                ? `${elapsedSeconds}s elapsed`
                : `${Math.floor(elapsedSeconds / 60)}m ${elapsedSeconds % 60}s elapsed`}
            </span>
            {totals?.totalRecords ? (
              <>
                <span aria-hidden>·</span>
                <span data-testid="install-setup-record-count">
                  {totals.totalRecords.toLocaleString()} records
                </span>
              </>
            ) : null}
          </div>
        </header>

        {/* Session 30.34 — Calm install: one progress bar + one line of
            detail. The step-by-step checklist only appears if something
            goes wrong, where it is actually useful for diagnosis. */}
        {!error ? (
          <Card className="rounded-[24px] border-border bg-card" data-testid="install-setup-progress-card">
            <CardContent className="space-y-3 p-6">
              <div className="h-2 w-full overflow-hidden rounded-full bg-secondary" aria-hidden>
                <div
                  className="h-full rounded-full bg-[hsl(var(--accent))] transition-[width] duration-300"
                  style={{ width: `${done ? 100 : Math.max(6, Math.round(((stepIndex + 0.5) / STEPS.length) * 100))}%` }}
                />
              </div>
              <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span className="truncate" data-testid="install-setup-current-step">
                  {done ? "Done" : STEPS[stepIndex]?.label}
                </span>
                <span className="shrink-0 tabular-nums">{done ? "100%" : `${Math.round(((stepIndex + 0.5) / STEPS.length) * 100)}%`}</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-[24px] border-border bg-card" data-testid="install-setup-progress-card">
          <CardContent className="space-y-3 p-6">
            <ol className="space-y-3">
              {STEPS.map((step, idx) => {
                const state = error && idx === stepIndex
                  ? "error"
                  : idx < stepIndex || done ? "done"
                  : idx === stepIndex ? "active"
                  : "pending";
                const isCurrent = state === "active" || state === "error";
                return (
                  <li
                    key={step.key}
                    className={`flex items-start gap-3 rounded-2xl border p-3 transition-colors ${
                      state === "error"
                        ? "border-red-500/40 bg-red-500/5"
                        : state === "done"
                          ? "border-emerald-500/40 bg-emerald-500/5"
                          : state === "active"
                            ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5"
                            : "border-border bg-background/40"
                    }`}
                    data-testid={`install-step-${step.key}`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                        state === "done"
                          ? "bg-emerald-500 text-white"
                          : state === "error"
                            ? "bg-red-500 text-white"
                            : state === "active"
                              ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                              : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {state === "done" ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : state === "error" ? (
                        <X className="h-3.5 w-3.5" />
                      ) : state === "active" ? (
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <span className="text-[10px] font-semibold">{idx + 1}</span>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${state === "pending" ? "text-muted-foreground" : ""}`}>
                        {step.label}
                      </p>
                      {isCurrent && stepDetail ? (
                        <p className={`mt-1 text-xs ${state === "error" ? "text-red-600" : "text-muted-foreground"}`} data-testid={`install-step-${step.key}-detail`}>
                          {stepDetail}
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>

        )}

        {error ? (
          <Card className="rounded-[24px] border-red-500/40 bg-red-500/5" data-testid="install-setup-error-card">
            <CardContent className="space-y-3 p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-red-600" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-red-700 dark:text-red-300">Offline setup is incomplete</p>
                  <p className="text-xs text-red-700/80 dark:text-red-300/80">{error}</p>
                </div>
              </div>
              {readinessReport && readinessReport.checks?.length ? (
                <ul className="space-y-1 rounded-2xl border border-red-500/30 bg-background/60 p-3 text-xs">
                  {readinessReport.checks
                    .filter((c) => !c.ok && c.severity !== "info")
                    .map((c, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <X className="mt-0.5 h-3.5 w-3.5 text-red-500" />
                      <div>
                        <span className="font-mono text-[11px] text-red-700 dark:text-red-300">{c.name}</span>
                        {c.detail ? <span className="ml-1 text-muted-foreground">— {c.detail}</span> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={retry}
                  data-testid="install-setup-retry"
                >
                  <RefreshCw className="h-4 w-4" /> Try again
                </Button>
                <Button
                  variant="ghost"
                  className="flex-1 rounded-xl"
                  onClick={skip}
                  data-testid="install-setup-skip"
                >
                  Continue anyway
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <p className="text-center text-[11px] text-muted-foreground">
          NFC Solutions · This device will operate independently of the cloud once setup completes.
        </p>
      </div>
    </main>
  );
}
